import { useEffect, useRef, useSyncExternalStore } from "react";

import { Alert, Snackbar } from "@mui/material";

import {
  advanceToastQueue,
  closeCurrentToast,
  getToastStateSnapshot,
  subscribeToToastState,
} from "./notifications";

export const SnackbarHost = () => {
  const state = useSyncExternalStore(
    subscribeToToastState,
    getToastStateSnapshot,
  );
  const current = state.queue[0];

  // autoHideDuration is managed manually (rather than via MUI's own prop)
  // so hovering can pause and resume it with the remaining time left.
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout>>();
  const remainingRef = useRef(0);
  const startedAtRef = useRef(0);

  useEffect(() => {
    if (!state.open || !current || current.autoClose === false) {
      return;
    }
    remainingRef.current = current.autoClose;
    startedAtRef.current = Date.now();
    const timeoutId = setTimeout(closeCurrentToast, current.autoClose);
    timeoutIdRef.current = timeoutId;
    return () => clearTimeout(timeoutId);
  }, [current, state.open]);

  if (!current) {
    return null;
  }

  const handleClose = (_event: unknown, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    closeCurrentToast();
  };

  const handleMouseEnter = () => {
    if (!current.pauseOnHover || current.autoClose === false) {
      return;
    }
    clearTimeout(timeoutIdRef.current);
    const elapsed = Date.now() - startedAtRef.current;
    remainingRef.current = Math.max(remainingRef.current - elapsed, 0);
  };

  const handleMouseLeave = () => {
    if (!current.pauseOnHover || current.autoClose === false) {
      return;
    }
    startedAtRef.current = Date.now();
    timeoutIdRef.current = setTimeout(closeCurrentToast, remainingRef.current);
  };

  const handleClick = () => {
    if (current.closeOnClick) {
      closeCurrentToast();
    }
  };

  return (
    <Snackbar
      key={current.key}
      open={state.open}
      autoHideDuration={null}
      onClose={handleClose}
      anchorOrigin={current.anchorOrigin}
      TransitionProps={{ onExited: advanceToastQueue }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Alert
        onClick={handleClick}
        onClose={closeCurrentToast}
        severity={current.severity}
        variant="filled"
        elevation={6}
        sx={{ width: "100%" }}
      >
        {current.message}
      </Alert>
    </Snackbar>
  );
};
